import React from 'react';
import {
  Typography,
  Container,
  Box,
  Paper,
  List,
  ListItem,
  Link,
  Fab,
} from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

const ScrollToTopButton = () => {
  const [visible, setVisible] = React.useState(false);
  const [fade, setFade] = React.useState(false);

  React.useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 200) {
        setVisible(true);
      } else {
        setVisible(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // Start fade-out transition
    setFade(true);
    // Remove the button after the fade-out completes
    setTimeout(() => {
      setVisible(false);
      setFade(false);
    }, 500); // Adjust duration to match CSS transition
  };

  if (!visible) return null;

  return (
    <Box
      onClick={handleClick}
      sx={{
        position: 'fixed',
        bottom: 16,
        right: 16,
        zIndex: 1000,
        opacity: fade ? 0 : 1,
        transition: 'opacity 0.5s ease-out',
      }}
    >
      <Fab color="primary" aria-label="scroll back to top">
        <KeyboardArrowUpIcon />
      </Fab>
    </Box>
  );
};

const TermsOfService = () => {
  return (
    <Container maxWidth="md">
      <Paper sx={{ p: 4, my: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Terms of Service
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" paragraph>
          Last updated: {new Date().toLocaleDateString()}
        </Typography>

        {/* Table of Contents */}
        <Box sx={{ my: 4 }}>
          <Typography variant="h6" gutterBottom>
            Table of Contents
          </Typography>
          <List>
            <ListItem>
              <Link href="#ai-notice" underline="hover">
                Important Notice Regarding AI-Generated Content
              </Link>
            </ListItem>
            <ListItem>
              <Link href="#acceptance" underline="hover">
                1. Acceptance of Terms
              </Link>
            </ListItem>
            <ListItem>
              <Link href="#service" underline="hover">
                2. Service Description
              </Link>
            </ListItem>
            <ListItem>
              <Link href="#disclaimer" underline="hover">
                3. Content Disclaimer
              </Link>
            </ListItem>
            <ListItem>
              <Link href="#subscription" underline="hover">
                4. Subscription and Payments
              </Link>
            </ListItem>
            <ListItem>
              <Link href="#responsibilities" underline="hover">
                5. User Responsibilities
              </Link>
            </ListItem>
            <ListItem>
              <Link href="#liability" underline="hover">
                6. Limitation of Liability
              </Link>
            </ListItem>
            <ListItem>
              <Link href="#contact" underline="hover">
                7. Contact Information
              </Link>
            </ListItem>
          </List>
        </Box>

        <Box id="ai-notice" sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom color="primary">
            Important Notice Regarding AI-Generated Content
          </Typography>
          <Typography
            paragraph
            sx={{ backgroundColor: '#f5f5f5', p: 2, borderRadius: 1 }}
          >
            ReaderFlex leverages state-of-the-art artificial intelligence to generate content for reading practice. Although we strive for high quality, this AI-generated content is intended solely for educational and practice purposes and may not be fully accurate. Please use this content only as a tool to improve your reading speed and comprehension.
          </Typography>
        </Box>

        <Box id="acceptance" sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom>
            1. Acceptance of Terms
          </Typography>
          <Typography paragraph>
            By accessing or using ReaderFlex, you agree to abide by these Terms of Service and our Privacy Policy. If you do not agree, please refrain from using the service.
          </Typography>
        </Box>

        <Box id="service" sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom>
            2. Service Description
          </Typography>
          <Typography paragraph>
            ReaderFlex is a speed reading training platform designed to enhance your reading speed and comprehension through:
          </Typography>
          <ul>
            <li>Dynamic speed reading exercises</li>
            <li>Personalized progress tracking and performance analytics</li>
            <li>Interactive comprehension quizzes</li>
            <li>Gamified milestones and achievement badges</li>
          </ul>
        </Box>

        <Box id="disclaimer" sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom>
            3. Content Disclaimer
          </Typography>
          <Typography paragraph>
            The content on ReaderFlex is generated using advanced AI technology. Please note:
          </Typography>
          <ul>
            <li>The content is for practice purposes only.</li>
            <li>We do not guarantee the accuracy, completeness, or reliability of the information.</li>
            <li>It should not be used as a source of verified facts.</li>
            <li>Any reliance on this content is at your own risk.</li>
          </ul>
        </Box>

        <Box id="subscription" sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom>
            4. Subscription and Payments
          </Typography>
          <Typography paragraph>
            ReaderFlex offers a trial period at a nominal fee of $1 for a specified duration. Following the trial, your subscription will automatically convert to a recurring monthly plan unless canceled. Key points include:
          </Typography>
          <ul>
            <li>Trial period fee: $1 for [X] days</li>
            <li>Automatic monthly renewal after the trial</li>
            <li>Cancellations can be made at any time via your account settings</li>
            <li>No refunds for partial months</li>
          </ul>
        </Box>

        <Box id="responsibilities" sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom>
            5. User Responsibilities
          </Typography>
          <Typography paragraph>
            As a user, you agree to:
          </Typography>
          <ul>
            <li>Provide accurate and current account information</li>
            <li>Keep your login credentials secure</li>
            <li>Use ReaderFlex solely for its intended purpose</li>
            <li>Avoid any attempts to manipulate or disrupt the service</li>
          </ul>
        </Box>

        <Box id="liability" sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom>
            6. Limitation of Liability
          </Typography>
          <Typography paragraph>
            ReaderFlex and its affiliates are not liable for any direct, indirect, incidental, or consequential damages arising from:
          </Typography>
          <ul>
            <li>Your use or inability to use the service</li>
            <li>Any reliance on AI-generated content</li>
            <li>Technical issues or interruptions in service</li>
          </ul>
        </Box>

        <Box id="contact" sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom>
            7. Contact Information
          </Typography>
          <Typography paragraph>
            If you have any questions or concerns about these Terms, please contact us at{' '}
            <Link href="mailto:terms@readerflex.com">terms@readerflex.com</Link>.
          </Typography>
        </Box>
      </Paper>
      {/* Scroll-to-top button */}
      <ScrollToTopButton />
    </Container>
  );
};

export default TermsOfService;
